import { matchPath } from 'react-router-dom';

import { capture } from './posthog';

// Centralized PostHog product-event taxonomy. Event names and property
// shapes live here so they aren't stringly-typed across the app (issue
// #251). Every track* function delegates to the guarded `capture` in
// posthog.ts, so all of this is a no-op until the SDK is configured.
//
// Client-intent vs server-authoritative: some gestures are persisted by a
// mutation whose authoritative event is emitted server-side in #253
// (`rating_created`). The frontend events below are the *intent/UX* signal for
// those gestures and must NOT duplicate the server names, or the same action
// double-counts. See AGENTS.md → Analytics.

// ---------------------------------------------------------------------------
// Pageviews
// ---------------------------------------------------------------------------

export type PageName =
  | 'feed'
  | 'request'
  | 'coffee_detail'
  | 'coffee_check_in'
  | 'account'
  | 'search'
  | 'admin'
  | 'admin_beta_users'
  | 'admin_request'
  | 'admin_roasters'
  | 'admin_listings_review'
  | 'admin_crawl_runs'
  | 'admin_green_coffee'
  | 'not_found';

interface RouteEntry {
  readonly pattern: string;
  readonly page_name: PageName;
}

// Mirrors the route set in App.tsx. ORDER MATTERS: matchPath is tried
// top-to-bottom and the first hit wins, so more specific patterns must
// precede their prefixes (`/coffee/:id/check-in` before `/coffee/:id`;
// `/admin/beta-users` before `/admin`). The `*` catch-all is the final
// fallback. Keep this in sync with App.tsx and the AGENTS.md page_name table.
const ROUTES: readonly RouteEntry[] = [
  { pattern: '/', page_name: 'feed' },
  { pattern: '/request', page_name: 'request' },
  { pattern: '/coffee/:id/check-in', page_name: 'coffee_check_in' },
  { pattern: '/coffee/:id', page_name: 'coffee_detail' },
  { pattern: '/me', page_name: 'account' },
  { pattern: '/search', page_name: 'search' },
  { pattern: '/admin/beta-users', page_name: 'admin_beta_users' },
  { pattern: '/admin/roasters/:roasterId/review', page_name: 'admin_listings_review' },
  { pattern: '/admin/roasters', page_name: 'admin_roasters' },
  { pattern: '/admin/crawl-runs', page_name: 'admin_crawl_runs' },
  { pattern: '/admin/green-coffee', page_name: 'admin_green_coffee' },
  { pattern: '/admin/request', page_name: 'admin_request' },
  { pattern: '/admin', page_name: 'admin' },
];

export interface ResolvedPage {
  // The matched route *pattern* (e.g. `/coffee/:id`), not the concrete URL,
  // so dynamic routes aggregate instead of fragmenting per coffee.
  readonly route: string;
  readonly page_name: PageName;
}

export function resolvePage(pathname: string): ResolvedPage {
  for (const entry of ROUTES) {
    if (matchPath({ path: entry.pattern, end: true }, pathname)) {
      return { route: entry.pattern, page_name: entry.page_name };
    }
  }
  // In dev, surface a path that fell through to the catch-all: it's either a
  // genuine 404 or a new App.tsx route missing from ROUTES (the latter would
  // otherwise bucket silently into not_found). Dev-only so prod 404s stay
  // quiet. The events.test.ts drift test is the CI-level guard.
  if (import.meta.env.DEV) {
    console.warn(
      `[analytics] no page_name for "${pathname}" — resolved to not_found. ` +
        'If this is a real route, add it to ROUTES in analytics/events.ts.',
    );
  }
  return { route: '*', page_name: 'not_found' };
}

export function trackPageView(page: ResolvedPage): void {
  // PostHog auto-attaches `$current_url`; we add the normalized route +
  // page_name so pages aggregate cleanly.
  capture('$pageview', { route: page.route, page_name: page.page_name });
}

// ---------------------------------------------------------------------------
// Search & its outcomes
// ---------------------------------------------------------------------------

export type SearchSurface = 'dropdown' | 'results_page';
export type SearchClickTarget = 'coffee' | 'view_all' | 'request';

// One logical search attempt (#448). Minted per settled, valid query and
// carried through search → click → coffee-view so a funnel can be built
// per-attempt, not just per person + timestamp.
export function newSearchId(): string {
  return crypto.randomUUID();
}

export function trackCoffeeSearched(props: {
  query: string;
  result_count: number;
  surface: SearchSurface;
  search_id: string;
}): void {
  capture('coffee_searched', props);
}

export function trackSearchResultClicked(props: {
  query: string;
  surface: SearchSurface;
  target: SearchClickTarget;
  // Only meaningful when target === 'coffee' (the row's index).
  position?: number;
  search_id: string;
}): void {
  capture('search_result_clicked', props);
}

// Router state the dropdown's "view all" passes to the results page (#448):
// the `search_id` of the dropdown attempt plus the query it was for, so the
// results page reuses the same id when its query matches instead of minting a
// new one.
export interface SearchResultsNavState {
  search_id: string;
  query: string;
}

// ---------------------------------------------------------------------------
// Browse / check-in
// ---------------------------------------------------------------------------

// The trusted internal entry points to a coffee detail page. Single source
// of truth: the `CoffeeViewSource` union and the runtime allowlist below both
// derive from this, so adding a value here can't drift them apart.
const INTERNAL_VIEW_SOURCES = [
  'feed',
  'search_dropdown',
  'search_results',
  'shelf',
  'check_in',
] as const;

const INTERNAL_VIEW_SOURCE_SET = new Set<string>(INTERNAL_VIEW_SOURCES);

// How the user reached the coffee detail page. A trusted internal link
// declares one of the internal values via React Router state; `direct` is the
// fallback for bookmarked and external visits and is never passed as state.
// CoffeeDetailPage consumes (clears) the state on first read so a full-page
// refresh — which restores `window.history.state` — resolves to `direct` too.
export type CoffeeViewSource = (typeof INTERNAL_VIEW_SOURCES)[number] | 'direct';

// The router-state shape trusted in-app links pass on navigation. Annotate
// call sites with `satisfies CoffeeViewNavState` so a mistyped source is a
// typecheck failure.
export interface CoffeeViewNavState {
  source: CoffeeViewSource;
  // Set only for search-originated views (#448): the `search_id` of the
  // attempt the click belonged to, so `coffee_viewed` joins the funnel.
  search_id?: string;
}

// Resolve the view source from untrusted `useLocation().state`. Only an
// allowlisted internal value passes through; anything else (absent state, a
// stray `direct`, a malformed object) is `direct`.
export function coffeeViewSourceFromNavState(state: unknown): CoffeeViewSource {
  if (state !== null && typeof state === 'object' && 'source' in state) {
    const { source } = state;
    if (typeof source === 'string' && INTERNAL_VIEW_SOURCE_SET.has(source)) {
      return source as CoffeeViewSource;
    }
  }
  return 'direct';
}

// Resolve the `search_id` (#448) from untrusted `useLocation().state`, parallel
// to `coffeeViewSourceFromNavState`. Returns the id only when it is a string;
// anything else (absent state, non-search view, malformed object) is undefined.
export function searchIdFromNavState(state: unknown): string | undefined {
  if (state !== null && typeof state === 'object' && 'search_id' in state) {
    const { search_id } = state;
    if (typeof search_id === 'string') {
      return search_id;
    }
  }
  return undefined;
}

export function trackCoffeeViewed(props: {
  coffee_id: string;
  source: CoffeeViewSource;
  search_id?: string;
}): void {
  capture('coffee_viewed', props);
}

// Client intent/UX signal for a check-in. The persisted/authoritative
// counterpart is the server-side `rating_created` (#253) — do not also emit
// `rating_created` from the frontend.
export function trackCoffeeCheckedIn(props: {
  coffee_id: string;
  score: number;
  has_notes: boolean;
  brew_type: string | null;
}): void {
  capture('coffee_checked_in', props);
}

// ---------------------------------------------------------------------------
// Link-first request (RFC 016)
// ---------------------------------------------------------------------------

// Client success signal for submitting a coffee/roaster request. Its
// server-authoritative counterpart is `request_submitted` (fired from the
// createRequest resolver) — do not also emit that name from the frontend.
export function trackRequestSent(props: { has_link: boolean }): void {
  capture('request_sent', props);
}

// ---------------------------------------------------------------------------
// Shelf
// ---------------------------------------------------------------------------

export type ShelfSurface = 'detail' | 'account';

export function trackShelfItemAdded(props: {
  coffee_id: string;
  surface: ShelfSurface;
}): void {
  capture('shelf_item_added', props);
}

export function trackShelfItemRemoved(props: {
  coffee_id: string;
  surface: ShelfSurface;
}): void {
  capture('shelf_item_removed', props);
}

// ---------------------------------------------------------------------------
// Auth (optional; complements the identify/reset already wired in
// PostHogIdentify / useLogout)
// ---------------------------------------------------------------------------

export function trackSignedIn(): void {
  capture('signed_in');
}

export function trackSignedOut(): void {
  capture('signed_out');
}

// `signed_in` must fire on an actual sign-in, not on every authenticated
// mount. PostHogIdentify re-mounts on each full page load while
// authenticated (refresh, new tab, email deep-link) because Auth0 resolves
// the session from its localStorage cache — a bare `trackSignedIn()` there
// would inflate with refresh frequency. We dedupe with a `sessionStorage`
// marker holding the current `sub`: it survives same-tab refreshes (no
// re-fire) but is absent in a new tab (a new session fires again), and a
// different `sub` (user switch) fires too. `useLogout` clears it so a
// re-login in the same tab fires again.
const SIGNED_IN_MARKER_KEY = 'wolisho:signed_in';

export function trackSignedInOncePerSession(sub: string): void {
  try {
    if (sessionStorage.getItem(SIGNED_IN_MARKER_KEY) === sub) return;
    sessionStorage.setItem(SIGNED_IN_MARKER_KEY, sub);
  } catch {
    // sessionStorage unavailable (privacy mode / quota) — emit anyway; a
    // possible duplicate beats a dropped sign-in signal.
  }
  trackSignedIn();
}

export function clearSignedInMarker(): void {
  try {
    sessionStorage.removeItem(SIGNED_IN_MARKER_KEY);
  } catch {
    // No marker to clear if storage is unavailable.
  }
}
